/**
 * Facebook Pixel — lazy, runtime loader. Like `./googleAnalytics`, the Pixel's `fbevents.js`
 * is injected on the FIRST call, never at module-eval time, so it loads only after the owner
 * has granted marketing consent. The pixel id comes from `window.config.facebookPixelId`.
 *
 * The initial `PageView` is NOT fired here — the app-wide route effect (`trackPageView`) owns
 * every page view so the first load isn't double-counted.
 */

// Injected once per page, at module scope so it survives React remounts.
let injected = false;

/* eslint-disable @typescript-eslint/no-explicit-any -- the fbq bootstrap is inherently dynamic */
/** The standard Facebook Pixel bootstrap (fbevents.js). Idempotent via `window.fbq`. */
function bootstrapPixel(): void {
  if (window.fbq) return;
  const n: any = (window.fbq = function (...args: any[]) {
    n.callMethod ? n.callMethod(...args) : n.queue.push(args);
  });
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  const t = document.createElement('script');
  t.async = true;
  t.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const s = document.getElementsByTagName('script')[0];
  s?.parentNode?.insertBefore(t, s);
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * Inject the Pixel and init `id` once. Idempotent. fbevents then sends only the calls the app makes: no page view on a
 * history change (`disablePushState`, the route effect owns page views) and no automatic button clicks or page metadata
 * (`autoConfig` off), which would carry labels and URLs the app never chose to send (Stats > Website's filters). Both
 * are read before `init`.
 */
export function loadFacebookPixel(id: string): void {
  if (injected || !id) return;
  injected = true;
  bootstrapPixel();
  const fbq = window.fbq;
  if (!fbq) return;
  fbq.disablePushState = true;
  fbq('set', 'autoConfig', false, id);
  fbq('init', id);
}

/** Grant / revoke Pixel consent. Safe when the Pixel isn't loaded (no-ops). */
export function fbSetConsent(granted: boolean): void {
  window.fbq?.('consent', granted ? 'grant' : 'revoke');
}

/** Record a page view. No-ops until fbq is present. */
export function fbPageView(): void {
  window.fbq?.('track', 'PageView');
}
